import React from "react";
import { Button, Icon } from "semantic-ui-react";
import { MUTED } from "./strategyCardTheme";

// The one Strategy Card used everywhere a Strategy (Pricing, Listing
// Description, Negotiations, Inspection) is offered to the agent, for both
// Buyer and Seller transactions - replaces the old, smaller StrategyMiniCard.
// Equal height within a CSS Grid row (the grid, not this component, handles
// stretching - see StrategyPropertyDashboard.jsx), primary action always
// pinned to the bottom via flex `justify-content: space-between`.
//
// `status` drives everything - never infer state from a combination of ad
// hoc booleans at the call site:
//   "generated"     - show `preview` (the result, in a tinted callout box using
//                      the strategy's own color) + a "View Strategy"-style action
//   "not_generated" - show `features` (a checklist, same tinted callout box) if
//                      given, else plain `description` text + a "Generate"-style action
//   "generating"    - show `description`, action is disabled + loading, label swaps to "Generating..."
//   "coming_soon"   - icon/title stay themed (never muted - only the bottom
//                      action bar is muted); `description` renders as usual,
//                      then a centered clock-badge "Coming Soon" callout with
//                      `comingSoonDetail` (falls back to `description` if not
//                      given) explaining what the feature will do
//   "restricted"    - existing entitlement/account-status gate failed; show `restrictedMessage`
//                      instead of a clickable action, never a misleadingly-available button
export default function StrategyCard({
  theme,
  description,
  status,
  preview,
  features,
  comingSoonDetail,
  lastUpdated,
  actionLabel,
  onAction,
  restrictedMessage,
  footnote,
}) {
  const isComingSoon = status === "coming_soon";
  const isRestricted = status === "restricted";
  const isGenerating = status === "generating";
  const isGenerated = status === "generated";
  const hasFeatureList = status === "not_generated" && Array.isArray(features) && features.length > 0;
  // Only the bottom action bar is muted for Coming Soon - the icon/title
  // keep the strategy's own color identity (matching the task's reference
  // mockup, where the Negotiations icon stays purple even while disabled).
  const iconColor = theme.color;
  const iconBg = theme.bg;

  const defaultActionLabel = isComingSoon
    ? "Coming Soon"
    : isGenerating
      ? "Generating…"
      : isGenerated
        ? "View Strategy"
        : "Generate";

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        height: "100%",
        background: "#ffffff",
        border: "1px solid #e0e1e2",
        borderRadius: "10px",
        padding: "20px",
      }}
    >
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "12px" }}>
          <div
            style={{
              width: 36, height: 36, borderRadius: "50%", background: iconBg,
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}
          >
            <Icon name={theme.icon} style={{ color: iconColor, margin: 0, fontSize: "1em" }} aria-hidden="true" />
          </div>
          <div style={{ fontWeight: 700, fontSize: "1em" }}>{theme.label}</div>
        </div>

        {isRestricted && restrictedMessage ? (
          <div className="text-grey" style={{ fontSize: "0.85em" }}>{restrictedMessage}</div>
        ) : (isGenerated && preview) || hasFeatureList ? (
          <div style={{ background: theme.bg, borderRadius: "8px", padding: "14px" }}>
            {isGenerated ? preview : (
              <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                {features.map((feature, i) => (
                  <li
                    key={feature}
                    style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "0.88em", marginBottom: i < features.length - 1 ? "8px" : 0 }}
                  >
                    <Icon name="check circle" style={{ color: theme.color, margin: 0 }} aria-hidden="true" />
                    {feature}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : (
          <div className="text-grey" style={{ fontSize: "0.85em", lineHeight: 1.4 }}>{description}</div>
        )}

        {isComingSoon && (
          <div style={{ textAlign: "center", marginTop: "16px", paddingTop: "16px", borderTop: "1px solid #f0f0f0" }}>
            <div
              style={{
                width: 32, height: 32, borderRadius: "50%", background: theme.bg,
                display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 8px",
              }}
            >
              <Icon name="clock outline" style={{ color: theme.color, margin: 0, fontSize: "0.95em" }} aria-hidden="true" />
            </div>
            <div style={{ fontWeight: 700, fontSize: "0.95em", color: theme.color }}>Coming Soon</div>
            <div className="text-grey" style={{ fontSize: "0.82em", lineHeight: 1.4, marginTop: "4px" }}>
              {comingSoonDetail || description}
            </div>
          </div>
        )}

        {isGenerated && lastUpdated && (
          <div className="text-grey" style={{ fontSize: "0.72em", marginTop: "8px" }}>
            <Icon name="check circle" color="green" style={{ fontSize: "0.95em" }} aria-hidden="true" />
            Generated {lastUpdated}
          </div>
        )}
      </div>

      {!isRestricted && (
        <Button
          fluid
          size="small"
          loading={isGenerating}
          disabled={isComingSoon || isGenerating}
          style={{
            marginTop: "16px",
            background: isComingSoon ? MUTED.bg : "#2185d0",
            color: isComingSoon ? MUTED.color : "#ffffff",
            cursor: isComingSoon ? "default" : "pointer",
          }}
          aria-label={`${actionLabel || defaultActionLabel} - ${theme.label}`}
          onClick={isComingSoon || isGenerating ? undefined : onAction}
        >
          {actionLabel || defaultActionLabel}
        </Button>
      )}

      {footnote && !isRestricted && (
        <div className="text-grey" style={{ fontSize: "0.78em", marginTop: "8px", display: "flex", alignItems: "center", gap: "6px" }}>
          <Icon name="info circle" style={{ margin: 0, fontSize: "0.95em" }} aria-hidden="true" />
          {footnote}
        </div>
      )}
    </div>
  );
}
